import type { BriefSynthesisView, Study } from '../api/client.ts';

/**
 * Where an `analyzing` study whose analysis has not been given up on has got to, as the Analyzing
 * step tells it. An escalated study is not at any of these: the step says why the wait stopped.
 *
 * - `reading` — no Brief has been asked for yet, so the analysis is still being waited on.
 * - `brief_next` — the analysis is ready and the Brief has been asked for: it is being written,
 *   or it is published and the next read of the study moves it to `complete`.
 * - `brief_failed` — the Brief could not be written and Try again is the way on.
 * - `declined` — too few people finished for a Brief to be written.
 */
export type AnalysingStage = 'reading' | 'brief_next' | 'brief_failed' | 'declined';

/**
 * The stage, decided from the Brief request the step has read. A request exists only once the
 * analysis is ready, since the readiness chain is what asks for it, so its absence is the wait for
 * the analysis.
 *
 * A `pending` request whose job the queue no longer holds is `retryable` and will never be
 * answered on its own, so it is a failure here as it is on the synthesis status line.
 */
export const fieldingStage = (
  synthesis: Pick<BriefSynthesisView, 'status' | 'retryable'> | null,
): AnalysingStage => {
  if (synthesis === null) {
    return 'reading';
  }

  switch (synthesis.status) {
    case 'published':
      return 'brief_next';
    case 'declined':
      return 'declined';
    case 'pending':
      return synthesis.retryable ? 'brief_failed' : 'brief_next';
    case 'unavailable':
    case 'unreadable':
      return 'brief_failed';
  }
};

/**
 * Whether the answers are still being worked on without the researcher: the analysis is awaited, or
 * the Brief is being written. The spinner on the Analyzing step and the hint beside its Next are both
 * decided by it. They part only over a published Brief that the study read has not caught up with:
 * the stage is `brief_next`, so the spinner stays on "Your brief is next.", while `briefOnItsWay`
 * withholds the hint, because the next read opens the Brief and moves the study on to it.
 */
export const isAnalysisInProgress = (stage: AnalysingStage): boolean =>
  stage === 'reading' || stage === 'brief_next';

/**
 * Whether a Brief is on its way without anything more from the researcher: the study is analysed,
 * its analysis has not been given up on, and either its Brief request has not been read yet or the
 * request is in progress (`isAnalysisInProgress`). Not once the Brief is published, since it is no
 * longer on its way and only the next read of the study stands between it and Next; not once the
 * Brief failed or was declined; and not in any other state.
 */
export const briefOnItsWay = ({
  state,
  escalation,
  brief,
}: {
  state: Study['state'] | null;
  escalation: Study['readinessEscalation'];
  brief: { loaded: boolean; synthesis: Pick<BriefSynthesisView, 'status' | 'retryable'> | null };
}): boolean => {
  if (state !== 'analyzing' || escalation !== null) {
    return false;
  }

  if (!brief.loaded) {
    return true;
  }

  return (
    brief.synthesis?.status !== 'published' && isAnalysisInProgress(fieldingStage(brief.synthesis))
  );
};
